import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, Card, SectionTitle, LoadingState, ErrorState, StatusBadge, EmptyState } from "@/components/common";
import { ProfileForm, SlugForm, BillingForm, HoursForm } from "@/components/pro/GarageForms";
import { ServicesManager } from "@/components/pro/ServicesManager";
import { EmployeesManager } from "@/components/pro/EmployeesManager";
import { SubscriptionPanel } from "@/components/pro/SubscriptionPanel";
import { ShareLinkCard } from "@/components/pro/ShareLinkCard";
import { PublicLinkCard } from "@/components/pro/PublicLinkCard";
import { SuspensionPolicy } from "@/components/pro/SuspensionPolicy";
import { ChecklistTemplate } from "@/modules/interventions/ChecklistTemplate";
import { ReminderSettings } from "@/modules/followups/ReminderSettings";
import { PaymentsSettings } from "@/modules/connect/HistoryReport";
import { DiagnosticTemplates } from "@/modules/diagnostics/Diagnostics";
import { JobTemplates } from "@/modules/quotes/JobTemplates";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { downloadFile, errMsg } from "@/lib/api";
import { fmtBytes, fmtDateTime } from "@/lib/format";
import { ShieldCheck } from "lucide-react";
import { t } from "@/i18n";

const TABS = ["garage", "public", "billing", "payments", "hours", "services", "diagnostics", "templates", "reminders", "plan", "support", "data"];
const MANAGER_TABS = ["billing", "payments", "templates", "reminders", "plan", "support", "data"];
const B9_TABS = { payments: ["b9.connect.tab", ""], diagnostics: ["b9.diag.templates", "b9.diag.templatesHint"], templates: ["b12.tpl.tab", "b12.tpl.hint"] };

function PlanUsage() {
  const { data, error, loading, reload } = useFetch("/garage/plan");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const p = data.plan;
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="plan-usage">
      <div className="rounded-lg border border-slate-200 p-4"><p className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("admin.col.plan")}</p><p className="mt-1 font-display text-2xl font-bold">{p?.name || "—"}</p><StatusBadge status={data.subscription.status} /></div>
      <div className="rounded-lg border border-slate-200 p-4"><p className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("employees.seats")}</p><p className="mt-1 font-display text-2xl font-bold">{data.seats_used} / {p?.max_internal_users ?? "—"}</p></div>
      <div className="rounded-lg border border-slate-200 p-4"><p className="text-xs uppercase tracking-[0.18em] text-slate-500">{t("admin.col.storage")}</p><p className="mt-1 font-display text-2xl font-bold">{fmtBytes(data.storage_bytes)} / {p?.photo_quota_mb ?? "—"} MB</p></div>
    </div>
  );
}

function SupportAccess() {
  const { garage } = useGarage();
  const { data, error, loading, reload } = useFetch("/garage/support-sessions");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!data.length) return <EmptyState icon={ShieldCheck} title={t("settings.noSupport")} text={t("settings.noSupportText")} />;
  return (
    <div className="space-y-3" data-testid="garage-support-sessions">
      {data.map((s) => (
        <div key={s.id} className="rounded-lg border border-slate-200 p-4 text-sm">
          <div className="flex items-center justify-between"><strong>{s.admin_email}</strong><StatusBadge status={s.active ? "active" : "ended"} /></div>
          <p className="mt-1 text-slate-700">“{s.reason}”</p>
          <p className="mt-1 text-xs text-slate-500">{t("support.timing", { start: fmtDateTime(s.started_at, garage.timezone), end: fmtDateTime(s.ended_at || s.expires_at, garage.timezone) })}</p>
        </div>
      ))}
    </div>
  );
}

function DataExport() {
  const { access } = useGarage();
  const dl = (params) => downloadFile("/garage/export", params).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="space-y-8">
      <div>
        <SectionTitle hint={t("settings.exportHint")}>{t("settings.export")}</SectionTitle>
        {access.role === "support" ? <p className="text-sm text-slate-500">{t("settings.exportSupport")}</p> : (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => dl({ format: "json" })} data-testid="export-json-button"><Download className="mr-2 h-4 w-4" /> {t("settings.exportJson")}</Button>
            <Button variant="outline" onClick={() => dl({ format: "csv", entity: "customers" })} data-testid="export-customers-csv-button"><Download className="mr-2 h-4 w-4" /> {t("settings.exportCustomersCsv")}</Button>
            <Button variant="outline" onClick={() => dl({ format: "csv", entity: "vehicles" })} data-testid="export-vehicles-csv-button"><Download className="mr-2 h-4 w-4" /> {t("settings.exportVehiclesCsv")}</Button>
          </div>
        )}
      </div>
      <SuspensionPolicy />
    </div>
  );
}

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const { access: acc } = useGarage();
  const tabs = acc.role === "mechanic" ? TABS.filter((k) => !MANAGER_TABS.includes(k)) : TABS;
  const tab = tabs.includes(params.get("tab")) ? params.get("tab") : "garage";
  const panels = {
    garage: <ProfileForm />, public: <div className="space-y-10"><ShareLinkCard /><SlugForm /><PublicLinkCard /></div>, billing: <BillingForm />,
    hours: <HoursForm />, services: <ServicesManager />, checklist: <ChecklistTemplate />, templates: <JobTemplates />, payments: <PaymentsSettings />, diagnostics: <DiagnosticTemplates />, reminders: <ReminderSettings />, plan: <div className="space-y-8"><SubscriptionPanel /><PlanUsage /></div>, support: <SupportAccess />, data: <DataExport />,
  };
  return (
    <div>
      <PageHeader title={t("pro.nav.settings")} subtitle={t("settings.subtitle")} />
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="mb-6 flex h-auto w-full flex-wrap justify-start gap-1 bg-white p-1 shadow-sm">
          {tabs.map((k) => <TabsTrigger key={k} value={k} data-testid={`settings-tab-${k}`} className="data-[state=active]:bg-[#0A2540] data-[state=active]:text-white">{B9_TABS[k] ? t(B9_TABS[k][0]) : t(`settings.tabs.${k}`)}</TabsTrigger>)}
        </TabsList>
        {tabs.map((k) => (
          <TabsContent key={k} value={k}>
            <Card>
              {k !== "payments" && <SectionTitle hint={B9_TABS[k] ? t(B9_TABS[k][1]) : t(`settings.hints.${k}`)}>{B9_TABS[k] ? t(B9_TABS[k][0]) : t(`settings.tabs.${k}`)}</SectionTitle>}
              {panels[k]}
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export function Employees() {
  return (
    <div>
      <PageHeader title={t("pro.nav.employees")} subtitle={t("employees.subtitle")} />
      <EmployeesManager />
    </div>
  );
}
